import React, { useEffect } from "react";
import { Formik, Form } from "formik";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Switch from "components/common/Switch";
import CustomField from "components/form/CustomField";
import { useSmartBack } from "hooks/useSmartBack";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getSystemSettings,
  updateSystemSettings,
} from "store/slices/pageContentSlice";
import MultiSelect from "components/common/MultiSelect";
import { handleSubmitForm } from "helpers/apiUtils";
import { PLAN_TYPES } from "utils/constants";

const AppSettings = () => {
  const { t } = useTranslation();
  const goBack = useSmartBack();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { systemSettings: fetchedSettings } = useSelector(
    (state) => state.PageContent,
  );

  const systemSettings = location.state?.systemSettingsData || fetchedSettings?.data;

  useEffect(() => {
    if (!location.state?.systemSettingsData) {
      dispatch(getSystemSettings());
    }
  }, []);

  // ─── Helper: find a setting block by key ───────────────────────────────────
  const getSetting = (key) => systemSettings?.find((s) => s.key === key);

  // ─── Derived data from API response ────────────────────────────────────────
  const forceUpdateSetting = getSetting("forceAppUpdate");
  const maintenanceSetting = getSetting("maintenanceMode");
  const pauseRuleSetting = getSetting("membershipPauseRule");
  const transferRuleSetting = getSetting("membershipTransferRule");
  const vatInfoSetting = getSetting("vatInfo");

  // ─── Initial values (fall back to safe defaults if API not loaded yet) ─────
  const forceUpdateInitial = {
    iOSApp: forceUpdateSetting?.value?.iOSApp ?? false,
    androidApp: forceUpdateSetting?.value?.androidApp ?? false,
    // iOSAppVersion:      forceUpdateSetting?.value?.iOSAppVersion  ?? "",
    // androidAppVersion:  forceUpdateSetting?.value?.androidAppVersion ?? "",
  };

  const maintenanceInitial = {
    iOSApp: maintenanceSetting?.value?.iOSApp ?? false,
    androidApp: maintenanceSetting?.value?.androidApp ?? false,
    website: maintenanceSetting?.value?.website ?? false,
  };

  const pauseRuleInitial = {
    maxCountPerCycle: pauseRuleSetting?.value?.maxCountPerCycle ?? "",
    minPauseDays: pauseRuleSetting?.value?.minPauseDays ?? "",
    maxPauseDays: pauseRuleSetting?.value?.maxPauseDays ?? "",
    allowedUnits: pauseRuleSetting?.value?.allowedUnits ?? [],
    allowedPlanTypes: pauseRuleSetting?.value?.allowedPlanTypes ?? [],
    autoResume: pauseRuleSetting?.value?.autoResume ?? false,
  };

  const transferRuleInitial = {
    maxTransfers: transferRuleSetting?.value?.maxTransfers ?? "",
    cooldownDays: transferRuleSetting?.value?.cooldownDays ?? "",
  };
  const vatInfoInitial = {
    title: vatInfoSetting?.value?.title ?? "",
    titleAr: vatInfoSetting?.value?.titleAr ?? "",
    value: vatInfoSetting?.value?.value ?? "",
    registeredNo: vatInfoSetting?.value?.registeredNo ?? "",
  };
  const allowedUnits = [
    { name: "Day", value: "day" },
    { name: "Month", value: "month" },
  ];
 

const createSettingSubmitHandler = (setting, buildPayload) => (values) => {
  if (!setting?._id) return;
  handleSubmitForm({
    dispatch,
    action: updateSystemSettings,
    payload: { id: setting._id, value: buildPayload(values) },
    onSuccess: () => navigate(-1),
  });
};
const handleForceUpdateSubmit = createSettingSubmitHandler(
  forceUpdateSetting,
  (v) => ({ iOSApp: v.iOSApp, androidApp: v.androidApp })
);

const handleMaintenanceSubmit = createSettingSubmitHandler(
  maintenanceSetting,
  (v) => ({ iOSApp: v.iOSApp, androidApp: v.androidApp, website: v.website })
);

const handlePauseRuleSubmit = createSettingSubmitHandler(
  pauseRuleSetting,
  (v) => ({
    maxCountPerCycle: Number(v.maxCountPerCycle),
    minPauseDays:     Number(v.minPauseDays),
    maxPauseDays:     Number(v.maxPauseDays),
    allowedUnits:     v.allowedUnits,
    allowedPlanTypes: v.allowedPlanTypes,
    autoResume:       v.autoResume,
  })
);

const handleTransferRuleSubmit = createSettingSubmitHandler(
  transferRuleSetting,
  (v) => ({
    maxTransfers: Number(v.maxTransfers),
    cooldownDays: Number(v.cooldownDays),
  })
);

const handleVatInfoSubmit = createSettingSubmitHandler(
  vatInfoSetting,
  (v) => ({
    title:        v.title,
    titleAr:      v.titleAr,
    value:        v.value,
    registeredNo: v.registeredNo,
  })
);
  return (
    <div className="page-content">
      <h2 className="page_title">{t("App Settings")}</h2>
      <h3 className="page_sub_title">
        {t("Configure app behavior, force updates, and maintenance mode")}
      </h3>

      {/* ──────────────────── FORCE APP UPDATE ──────────────────── */}
      <div className="common_card mb-4">
        <h4 className="section_title">{t("Force App Update")}</h4>

        <Formik
          initialValues={forceUpdateInitial}
          enableReinitialize // ← re-init when API data arrives
          onSubmit={handleForceUpdateSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Row className="g-4">
                {/* iOS Toggle */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>{t("Enable Force Update for iOS")}</label>
                    <Switch
                      checked={values.iOSApp}
                      handleOnChange={(e) => setFieldValue("iOSApp", e.value)}
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "If enabled, iOS users will be forced to update the app to the version you specify below.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* iOS Version */}
                {/* <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("iOS App Version")}
                      name="iOSAppVersion"
                      placeholder="1.0.0"
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "Enter the minimum iOS app version required. Users on older versions will not be able to use the app until they update."
                      )}
                    </p>
                  </div>
                </Col> */}

                {/* Android Toggle */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>{t("Enable Force Update for Android")}</label>
                    <Switch
                      checked={values.androidApp}
                      handleOnChange={(e) =>
                        setFieldValue("androidApp", e.value)
                      }
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "If enabled, Android users will be forced to update the app to the version you specify below.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Android Version */}
                {/* <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Android App Version")}
                      name="androidAppVersion"
                      placeholder="1.0.0"
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "Enter the minimum Android app version required. Users on older versions must update to continue using the app."
                      )}
                    </p>
                  </div>
                </Col> */}

                {/* Buttons */}
                <Col lg={12} className="mt-2 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save Settings")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>

      {/* ──────────────────── MAINTENANCE MODE ──────────────────── */}
      <div className="common_card">
        <h4 className="section_title">{t("Maintenance Mode")}</h4>

        <Formik
          initialValues={maintenanceInitial}
          enableReinitialize // ← re-init when API data arrives
          onSubmit={handleMaintenanceSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Row className="g-4">
                {/* iOS Maintenance */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>{t("Enable Maintenance for iOS")}</label>
                    <Switch
                      checked={values.iOSApp}
                      handleOnChange={(e) => setFieldValue("iOSApp", e.value)}
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "If enabled, iOS users will see a maintenance mode message and cannot use the app.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Android Maintenance */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>{t("Enable Maintenance for Android")}</label>
                    <Switch
                      checked={values.androidApp}
                      handleOnChange={(e) =>
                        setFieldValue("androidApp", e.value)
                      }
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "If enabled, Android users will see a maintenance mode message and cannot use the app.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Website Maintenance */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>{t("Enable Maintenance for Website")}</label>
                    <Switch
                      checked={values.website}
                      handleOnChange={(e) => setFieldValue("website", e.value)}
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "If enabled, website users will see a maintenance mode message and cannot access the site.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Buttons */}
                <Col lg={12} className="mt-2 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save Settings")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
      {/* ──────────────────── MEMBERSHIP PAUSE RULE ──────────────────── */}
      <div className="common_card mt-4">
        <h4 className="section_title">{t("Membership Pause Rule")}</h4>

        <Formik
          initialValues={pauseRuleInitial}
          enableReinitialize
          onSubmit={handlePauseRuleSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Row className="g-4">
                {/* Max Count Per Cycle */}
                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Max Pauses Per Cycle")}
                      name="maxCountPerCycle"
                      type="number"
                      placeholder="50"
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "Maximum number of times a membership can be paused within one billing cycle.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Min Pause Days */}
                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Minimum Pause Days")}
                      name="minPauseDays"
                      type="number"
                      placeholder="1"
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "The minimum number of days a membership can be paused at a time.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Max Pause Days */}
                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Maximum Pause Days")}
                      name="maxPauseDays"
                      type="number"
                      placeholder="30"
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "The maximum number of days a membership can be paused at a time.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Allowed Units */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>{t("Allowed Pause Units")}</label>
                    <div className="d-flex gap-3 mt-1">
                      <MultiSelect
                        required
                        name="allowedUnits"
                        value={values.allowedUnits}
                        options={allowedUnits}
                        onChange={(val) => setFieldValue("allowedUnits", val)}
                      />
                    </div>

                    <p className="text-muted small mt-1">
                      {t(
                        "Select which time units members can choose when pausing their membership.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Allowed Plan Types */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>{t("Allowed Plan Types")}</label>
                    <div className="d-flex gap-3 mt-1">
                      <MultiSelect
                        // label={t("Allowed Plan Types")}
                        required
                        name="allowedPlanTypes"
                        value={values.allowedPlanTypes}
                        options={PLAN_TYPES}
                        onChange={(val) =>
                          setFieldValue("allowedPlanTypes", val)
                        }
                      />
                    </div>
                    <p className="text-muted small mt-1">
                      {t(
                        "Only members on selected plan types will be allowed to pause their membership.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Auto Resume */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>{t("Auto Resume")}</label>
                    <Switch
                      checked={values.autoResume}
                      handleOnChange={(e) =>
                        setFieldValue("autoResume", e.value)
                      }
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "If enabled, paused memberships will automatically resume after the pause period ends.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Buttons */}
                <Col lg={12} className="mt-2 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save Settings")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>

      {/* ──────────────────── MEMBERSHIP TRANSFER RULE ──────────────────── */}
      <div className="common_card mt-4">
        <h4 className="section_title">{t("Membership Transfer Rule")}</h4>

        <Formik
          initialValues={transferRuleInitial}
          enableReinitialize
          onSubmit={handleTransferRuleSubmit}
        >
          {({ values }) => (
            <Form>
              <Row className="g-4">
                {/* Max Transfers */}
                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Max Transfers")}
                      name="maxTransfers"
                      type="number"
                      placeholder="50"
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "Maximum number of times a membership can be transferred to another member.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Cooldown Days */}
                {/* <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Cooldown Days")}
                      name="cooldownDays"
                      type="number"
                      placeholder="7"
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "Number of days a member must wait before they can transfer their membership again.",
                      )}
                    </p>
                  </div>
                </Col> */}

                {/* Buttons */}
                <Col lg={12} className="mt-2 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save Settings")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
      {/* ──────────────────── VAT INFO ──────────────────── */}
      <div className="common_card mt-4">
        <h4 className="section_title">{t("VAT Rule")}</h4>

        <Formik
          initialValues={vatInfoInitial}
          enableReinitialize
          onSubmit={handleVatInfoSubmit}
        >
          {({ values }) => (
            <Form>
              <Row className="g-4">
                {/* Max Transfers */}
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Title")}
                      name="title"
                      type="text"
                      placeholder="Title"
                      enableTranslate
                      translateTo="ar"
                      targetField="titleAr"
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Title Arabic")}
                      name="titleAr"
                      type="text"
                      placeholder="Title ar"
                      enableTranslate
                      translateTo="ar"
                      targetField="title"
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Value")}
                      name="value"
                      type="number"
                      placeholder="value"
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Registered Number")}
                      name="registeredNo"
                      type="number"
                      placeholder="Registered Number"
                    />
                  </div>
                </Col>
                {/* Buttons */}
                <Col lg={12} className="mt-2 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save Settings")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AppSettings;
